import { Component, type ErrorInfo, type ReactNode } from 'react';

type Props = {
  children: ReactNode;
  fallback?: (err: Error, reset: () => void) => ReactNode;
};

type State = {
  error: Error | null;
};

/**
 * Error boundary global. Captura crashes em qualquer descendente e mostra
 * um fallback amigável em vez de tela branca. Útil para erros de render,
 * não cobre erros assíncronos (Promise rejections — para isso use toast.error
 * + onError do React Query).
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[NOUS] Error boundary captured:', error, info.componentStack);
  }

  reset = () => {
    this.setState({ error: null });
  };

  override render() {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      return <DefaultFallback error={this.state.error} reset={this.reset} />;
    }
    return this.props.children;
  }
}

function DefaultFallback({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <main className="min-h-screen grid place-items-center px-6 py-12">
      <div className="mg-card p-8 max-w-xl w-full">
        <p className="mg-eyebrow mb-3 text-error/80">/ erro inesperado</p>
        <h1 className="mg-h1 text-3xl mb-3">Algo deu errado.</h1>
        <p className="text-fg-muted mb-6">
          A página encontrou um erro de renderização. Você pode tentar de novo, voltar ao
          painel ou recarregar a aba. Os dados estão seguros — isto é apenas a UI.
        </p>
        <details className="mg-card p-4 mb-6 bg-fg/[0.02]">
          <summary className="font-mono text-xs text-fg-muted cursor-pointer">
            ver detalhes técnicos
          </summary>
          <pre className="font-mono text-xs text-fg-muted/80 mt-3 whitespace-pre-wrap break-words">
            {error.name}: {error.message}
            {error.stack && '\n\n' + error.stack.slice(0, 1500)}
          </pre>
        </details>
        <div className="flex flex-wrap gap-3">
          <button onClick={reset} className="mg-button-primary">
            Tentar de novo
          </button>
          <a href="/app/dashboard" className="mg-button-ghost">
            Voltar ao painel
          </a>
          <button
            onClick={() => window.location.reload()}
            className="mg-button-ghost"
          >
            Recarregar
          </button>
        </div>
      </div>
    </main>
  );
}
